import { useCallback } from 'react';

import { AgGridReact } from '@ag-grid-community/react';
import { isEqual } from 'lodash-es';

import { ApiTableDataType, PricingDataType, SelectedData, OperationType } from '../types';
import { calculateKitCost } from '../utils';
import { SECTION_TYPE } from '../constants';
import { BulkEditPayload } from '../../pricing&LeadTime/bulkEdit/bulkEditPopup/queries/apiTypes';
import { convertToDollar } from 'src/features/shared/utils/utils';
import { invalidatePricingBodQuery } from '../../pricing&LeadTime/queries';

interface IPricingComponentData {
    id: number;
    qty: number | null;
    cost: number | null;
    discountedCost: number | null;
    sellPrice: number | null;
    is_hidden: boolean;
    per: number;
    is_kit: boolean;
    kit: number | null;
}

interface IRowDragPayload {
    section_type?: SECTION_TYPE;
    section_id: number;
    previous_bod_id: string | number | null;
    bod_id: number[];
    kit_id?: number | null;
}

export interface HandleKitPricingParamsType {
    onPricingInfoEdit: (info: PricingDataType, shouldCancelQueries?: boolean) => Promise<void>;
    tableRef: React.RefObject<AgGridReact<any>>;
    type: OperationType;
    productIds?: number[];
    componentsFromPartialKits?: SelectedData[];
    isHidden?: boolean;
    rowDragPayload?: IRowDragPayload;
    bulkEditPayload?: BulkEditPayload[];
}

function getComponentsCount(bodIds: number[], bodsMap: Map<number, IPricingComponentData>) {
    let componentsCount = 0;
    bodIds.forEach((id) => {
        if (bodsMap.has(id) && bodsMap.get(id)?.kit) {
            componentsCount++;
        }
    });
    return componentsCount;
}

const prevKitPrices = new Map();

export const useKitPricingUpdate = (bomId: string) => {
    const handleKitPricingUpdate = useCallback(
        async <T extends ApiTableDataType>({ onPricingInfoEdit, type, tableRef, productIds, componentsFromPartialKits, isHidden, rowDragPayload, bulkEditPayload }: HandleKitPricingParamsType) => {
            if ((Array.isArray(productIds) && !productIds.length) || (Array.isArray(componentsFromPartialKits) && !componentsFromPartialKits.length)) return;

            const kitsComponentMap = new Map();
            const bodsMap = new Map<number, IPricingComponentData>();
            let shouldCancelQueries = true;
            let isApiCallMade = false;

            // Step 1 :: Prepare Bods Map
            tableRef?.current?.api.forEachNode((rowNode) => {
                const { data } = rowNode;
                if (data.groupPath.length > 1) {
                    const { id, qty, cost, discountedCost, sellPrice, is_hidden, per, is_kit, kit } = data;
                    bodsMap.set(id, {
                        id,
                        qty,
                        cost,
                        discountedCost,
                        sellPrice,
                        is_hidden,
                        per,
                        is_kit,
                        kit
                    });
                }
            });
            // making sure, that we proceed with updating kit cost only when we have component(s) in the productIds list for certain operation types, else return early.
            if (productIds?.length && bodsMap.size && !['product-move-into-kit', 'drag-drop', 'copy-paste'].some((t) => t === type) && !getComponentsCount(productIds, bodsMap)) {
                return;
            }

            // Step 2 : Manipulate bodsMap based on the operation types
            switch (type) {
                case 'delete':
                    // when single delete component
                    if (productIds?.length === 1 && bodsMap.get(productIds[0])?.kit) {
                        bodsMap.delete(productIds[0]);
                    } else if (componentsFromPartialKits?.length) {
                        // when bulk delete components
                        componentsFromPartialKits.forEach((item) => {
                            bodsMap.delete(item.id);
                        });
                    }
                    break;

                case 'duplicate':
                    // when single duplicate component
                    if (productIds?.length === 1 && bodsMap.get(productIds[0])?.kit) {
                        const duplicatedBod = bodsMap.get(productIds[0]);
                        if (duplicatedBod) {
                            duplicatedBod.id = 1;
                            bodsMap.set(duplicatedBod.id, duplicatedBod);
                        }
                    } else if (componentsFromPartialKits?.length) {
                        // when bulk duplicate bods
                        let initialBodId = 1;
                        componentsFromPartialKits.forEach((item) => {
                            const duplicatedBod = bodsMap.get(item.id);
                            if (duplicatedBod) {
                                duplicatedBod.id = initialBodId++;
                                bodsMap.set(duplicatedBod.id, duplicatedBod);
                            }
                        });
                    }
                    shouldCancelQueries = false;
                    break;

                case 'hide-unhide':
                    if (productIds?.length && isHidden != null) {
                        productIds.forEach((id) => {
                            const component = bodsMap.get(id);
                            if (component && component.kit) {
                                component.is_hidden = isHidden;
                            }
                        });
                    }
                    shouldCancelQueries = false;
                    break;

                case 'component-move-out':
                    if (productIds?.length) {
                        const component = bodsMap.get(productIds[0]);
                        if (component && component.kit) {
                            component.kit = null;
                        }
                    }
                    break;

                case 'product-move-into-kit':
                    if (productIds?.length) {
                        const kitId = productIds[0];
                        const movingProductId = productIds[1];
                        const currentProduct = bodsMap.get(movingProductId);
                        if (currentProduct && bodsMap.has(kitId)) {
                            currentProduct.kit = kitId;
                        }
                    }
                    break;

                case 'drag-drop':
                    if (rowDragPayload) {
                        let isDragOperationPerformedOverComponentsOrKits = false;
                        // we can have products as well as components
                        rowDragPayload.bod_id.forEach((id) => {
                            const draggableBod = bodsMap.get(id);
                            if (draggableBod) {
                                // if bod is component and being moved to another kit or if bod is not component and being moved to kit
                                if ((draggableBod?.kit && rowDragPayload?.kit_id) || (!draggableBod?.kit && rowDragPayload?.kit_id)) {
                                    draggableBod.kit = rowDragPayload.kit_id;
                                    isDragOperationPerformedOverComponentsOrKits = true;
                                    // if bod is component and being moved out of kit
                                } else if (draggableBod?.kit && !rowDragPayload?.kit_id) {
                                    draggableBod.kit = null;
                                    isDragOperationPerformedOverComponentsOrKits = true;
                                }
                            }
                        });

                        // return early if drag drop operation was not performed over kits or components
                        if (!isDragOperationPerformedOverComponentsOrKits) {
                            return;
                        }
                        shouldCancelQueries = false;
                    }
                    break;

                case 'bulk-edit':
                    if (bulkEditPayload?.length) {
                        // check if the payload contains the components ! If not return early
                        let doComponentsExists = false;

                        bulkEditPayload.forEach((item) => {
                            const product = bodsMap.get(item?.bod_data?.bod_id);
                            if (product && product.kit && (item?.metadata?.cost_cents != null || item?.metadata?.discounted_cost_cents != null || item?.metadata?.sell_price_cents != null)) {
                                if (!doComponentsExists) {
                                    doComponentsExists = true;
                                }
                                product.qty = item?.bod_data?.qty != null && !Number.isNaN(item?.bod_data?.qty) ? item.bod_data.qty : product.qty;
                                product.cost = item?.metadata?.cost_cents ? convertToDollar(item.metadata.cost_cents) : product.cost;
                                product.discountedCost = item?.metadata?.discounted_cost_cents ? convertToDollar(item.metadata.discounted_cost_cents) : product.discountedCost;
                                product.sellPrice = item?.metadata?.sell_price_cents ? convertToDollar(item.metadata.sell_price_cents) : product.sellPrice;
                            }
                        });

                        if (!doComponentsExists) {
                            return;
                        }
                    }
                    break;
                default:
                    break;
            }

            if (bodsMap.size) {
                // step 3 : Initialize the components array with kit line data
                for (let [bodId, bod] of bodsMap.entries()) {
                    // Check if bod is kit
                    if (bod.is_kit) {
                        kitsComponentMap.set(bodId, [bodsMap.get(bodId)]);
                    }
                }

                // step 4 : For nodes with 'kit' data, add them to kitsComponentMap
                for (let bod of bodsMap.values()) {
                    // Check if 'bod.kit' exists and 'kitsComponentMap' has an entry for 'bod.kit'
                    if (bod.kit && kitsComponentMap.has(bod.kit)) {
                        const kitArray = kitsComponentMap.get(bod.kit);
                        if (kitArray) {
                            kitArray.push(bod); // Safely push into the array if it's defined
                        }
                    }
                }
            }

            // step 5 : Prepare the kit cost and then make the PATCH api call
            for (let [kitId, components] of kitsComponentMap.entries()) {
                try {
                    if (components.length > 1) {
                        const kitCostData = calculateKitCost(components.slice(1), (components[0] as T)?.qty || 0);
                        const prevKitCostData = prevKitPrices.get(kitId);
                        if (prevKitCostData && isEqual(kitCostData, prevKitCostData)) {
                            continue;
                        }
                        if (kitCostData) {
                            prevKitPrices.set(kitId, kitCostData);
                            await onPricingInfoEdit({ ...kitCostData, id: kitId }, shouldCancelQueries);
                            if (!isApiCallMade) {
                                isApiCallMade = true;
                            }
                        }
                    }
                } catch (e) {
                    console.error(`Error while updating cost of kit id : ${kitId}`);
                }
            }

            if (type === 'bulk-edit' && bomId && isApiCallMade) {
                invalidatePricingBodQuery(Number(bomId));
            }
        },
        [bomId]
    );

    return {
        handleKitPricingUpdate
    };
};
